import {
  BaSeButton,
  BaSeHeading6,
  BaSeInput,
  BaSeSelect,
  BaSeSmall1,
  BaSeTheme,
} from '@base/react';
import { format } from 'date-fns';
import React from 'react';
import { useCandidateController } from '../../../../hooks/controllers/candidate-controller';
import { useInterviewController } from '../../../../hooks/controllers/interview-controller';
import { useReportController } from '../../../../hooks/controllers/report-controller';
import { useMessage } from '../../../../hooks/messages/useMessage';
import { useModule } from '../../../../hooks/module/module';
import { cpfMask } from '../../../../utils/formatters/cpfMask';
import { cpfValidator } from '../../../../utils/validators/cpf-validator';
import { AlertWrapper, ButtonWrapper, ContentModal } from './interview-styled';

const REQUIRED_FIELD = 'Campo obrigatório não informado!';
const INVALID_CPF = 'CPF inválido!';
const NAO_PREENCHIDO = 'NAO_PREENCHIDO';
const EM_PREENCHIMENTO = 'EM_PREENCHIMENTO';
const EXPIRADA = 'EXPIRADA';

const MODULES = {
  1: 'URBANO',
  2: 'STARTUP',
  3: 'ARENA',
  4: 'RURAL',
};

function formatNameModule(string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const FindCandidate = (props) => {
  const [cpfComplement, setCpfComplement] = React.useState();
  const [cpf, setCpf] = React.useState(props.interview?.candidate?.cpf || '');
  const {
    getActiveInterviewByCpf,
    getLastIntervewByCpfAndModule,
    getCertificatedate,
    getValidatePreRegistration,
    postMigrateInterviews,
  } = useInterviewController();

  const { getResultReport, downloadPdf } = useReportController();
  const { handleCustomSuccess } = useMessage();

  const { getCandidateByCpf, candidateLoading } = useCandidateController();
  const { getStatusInterview } = useInterviewController();
  const [status, setStatus] = React.useState('');
  const [interviewExpired, setInterviewExpired] = React.useState(false);
  const [interviewExpiredData, setInterviewExpiredData] = React.useState();
  const [preRegistration, setPreRegistration] = React.useState();
  const [disableForms, setDisableForms] = React.useState(false);
  const [certificateInterview, setCertificateInterview] = React.useState(false);
  const [certificateInterviewData, setCertificateInterviewData] =
    React.useState();

  const { modules, moduleSelected } = useModule();
  const [moduleSelect, setModuleSelect] = React.useState(
    props.interview?.module?.id,
  );

  const onValidate = React.useCallback(() => {
    const complement = !cpf
      ? REQUIRED_FIELD
      : !cpfValidator(cpf)
        ? INVALID_CPF
        : '';
    setCpfComplement(complement);
    return complement;
  }, [cpf]);

  const completeSearch = React.useCallback(
    (activeInterview) => {
      if (
        activeInterview.statusCompletingInterview === NAO_PREENCHIDO ||
        activeInterview.statusCompletingInterview === EM_PREENCHIMENTO ||
        (activeInterview.module.id === 4 &&
          activeInterview.statusCompletingInterview !== EXPIRADA)
      ) {
        props.emitEvent('editInterview', activeInterview);
        props.closeModal('editInterview');
        return;
      }
      props.emitEvent('activeInterview', activeInterview);
      props.closeModal('activeInterview');
    },
    [props],
  );

  const migrate = React.useCallback(
    (data) => {
      postMigrateInterviews(data)
        .then((response) => {
          const { interview } = preRegistration;
          const { candidate } = interview;
          handleCustomSuccess(
            'Dados Importados com Sucesso!',
            `
          O resultado da entrevista foi importado para o candidato: ${candidate.name
            } de CPF: ${cpfMask(candidate.cpf)} do Empretec ${formatNameModule(
              data.moduleOrigin,
            )} para o Empretec ${formatNameModule(data.moduleDestiny)}.`,
          );
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          props.closeModal('close');
        });
    },
    [handleCustomSuccess, postMigrateInterviews, preRegistration, props],
  );

  const AceptedImportUser = React.useCallback(() => {
    const { candidate } = preRegistration.interview;
    const data = {
      cpf: candidate.cpf,
      moduleOrigin: MODULES[preRegistration.interview.module.id],
      moduleDestiny: MODULES[moduleSelect],
    };
    migrate(data);
  }, [migrate, moduleSelect, preRegistration]);

  const Sim = React.useCallback(
    (e) => {
      props.emitEvent('expiredInterview', interviewExpiredData);
      props.closeModal('expiredInterview');
    },
    [interviewExpiredData, props],
  );

  const Nao = React.useCallback(() => {
    props.closeModal('close');
  }, [props]);

  const goNewCandidate = React.useCallback(
    (cleanCpf) => {
      getCandidateByCpf(cleanCpf)
        .then((candidate) => {
          props.emitEvent('candidate', { candidate, moduleSelect });
          props.closeModal('candidate');
        })
        .catch((e) => {
          e.error = `Erro ao tentar buscar o candidato pelo CPF ${cleanCpf}`;
          props.closeModal(e);
        });
    },
    [getCandidateByCpf, props, moduleSelect],
  );

  const validPreRegistration = React.useCallback(() => {
    props.emitEvent('candidate', preRegistration?.interview);
    props.closeModal('candidate');
  }, [preRegistration, props]);

  const handleLastInterview = React.useCallback(
    (lastInterview, cleanCpf) => {
      if (lastInterview?.statusCompletingInterview === 'EXPIRADA') {
        setInterviewExpired((prev) => !prev);
        setInterviewExpiredData(lastInterview);
      } else {
        goNewCandidate(cleanCpf);
      }
    },
    [goNewCandidate],
  );

  const handleActiveInterview = React.useCallback(
    async (activeInterview) => {
      if (activeInterview.statusCompletingInterview === 'CERTIFICADA') {
        const response = await getCertificatedate(
          moduleSelect,
          activeInterview?.candidate?.cpf,
        );
        setCertificateInterview((prev) => !prev);
        setCertificateInterviewData(response);
        setInterviewExpiredData(activeInterview);
      } else {
        completeSearch(activeInterview);
      }
    },
    [completeSearch, getCertificatedate, moduleSelect],
  );

  const handleModuleOne = React.useCallback(
    async (cleanCpf) => {
      const activeInterview = await getActiveInterviewByCpf(cleanCpf);

      if (activeInterview) {
        await handleActiveInterview(activeInterview);
      } else {
        const lastInterview = await getLastIntervewByCpfAndModule(
          cleanCpf,
          moduleSelect,
        );
        handleLastInterview(lastInterview, cleanCpf);
      }
    },
    [
      getActiveInterviewByCpf,
      getLastIntervewByCpfAndModule,
      handleActiveInterview,
      handleLastInterview,
      moduleSelect,
    ],
  );

  const handleModuleOther = React.useCallback(
    async (cleanCpf) => {
      const validateDto = await getValidatePreRegistration(
        moduleSelect,
        cleanCpf,
      );

      if (!validateDto?.valid) {
        if (validateDto?.editInfo) {
          completeSearch(validateDto.interview);
        } else {
          setPreRegistration(validateDto);
          setDisableForms(!validateDto.valid);
        }
      } else {
        goNewCandidate(cleanCpf);
      }
    },
    [completeSearch, getValidatePreRegistration, goNewCandidate, moduleSelect],
  );

  const find = React.useCallback(
    async (e) => {
      if (onValidate()) return;

      const cleanCpf = cpf.replace(/[^\d]/g, '');
      if (cleanCpf.length !== 11) return;

      try {
        if (moduleSelect === 1) {
          await handleModuleOne(cleanCpf);
        } else {
          await handleModuleOther(cleanCpf);
        }
      } catch (error) {
        console.error('Erro na execução da função find:', error);
      }
    },
    [cpf, handleModuleOne, handleModuleOther, moduleSelect, onValidate],
  );

  const primaryButtom = React.useCallback(
    (value, action, disable) => {
      return (
        <BaSeButton
          buttonType="button"
          width="100px"
          color={BaSeTheme.colors.institucionais.azulSebrae10}
          type={'primary'}
          loadingText="Carregando..."
          isLoading={candidateLoading}
          value={value}
          onClick={action}
          isDisabled={disable}
        />
      );
    },
    [candidateLoading],
  );

  const seconderyButtom = React.useCallback(
    (value, action, disable) => {
      return (
        <BaSeButton
          buttonType="button"
          width="100px"
          color={BaSeTheme.colors.institucionais.cinzaSebrae45}
          type={'secondary'}
          loadingText="Carregando..."
          isLoading={candidateLoading}
          value={value}
          onClick={action}
          isDisabled={disable}
        />
      );
    },
    [candidateLoading],
  );

  const okButtom = React.useCallback(
    () => primaryButtom('Ok', () => props.closeModal('close')),
    [primaryButtom, props],
  );

  const optionsButtons = React.useCallback(
    (yes, no, yesDisable, noDisable) => {
      return (
        <ButtonWrapper>
          {primaryButtom('Sim', yes, yesDisable)}
          {seconderyButtom('Não', no, noDisable)}
        </ButtonWrapper>
      );
    },
    [primaryButtom, seconderyButtom],
  );

  const resolverButtons = React.useCallback(
    (preRegistration) => {
      if (preRegistration?.certificated) {
        return okButtom();
      }

      if (preRegistration?.finishOrExpired) {
        return optionsButtons(validPreRegistration, Nao, false, false);
      }

      if (preRegistration?.importData) {
        return optionsButtons(AceptedImportUser, Nao, false, false);
      }

      if (preRegistration?.evaluation) {
        return (
          <BaSeButton
            buttonType="button"
            width="250px"
            color={BaSeTheme.colors.institucionais.azulSebrae10}
            type={'primary'}
            loadingText="Carregando..."
            isLoading={candidateLoading}
            value={'Acessar questionário de seleção'}
            onClick={() => {
              props.closeModal('close');
            }}
          />
        );
      }

      if (preRegistration?.showQuiz) {
        return (
          <BaSeButton
            buttonType="button"
            color={BaSeTheme.colors.institucionais.azulSebrae10}
            type={'primary'}
            loadingText="Carregando..."
            isLoading={candidateLoading}
            value={'Visualizar questionário de seleção'}
            onClick={() => {
              props.closeModal('close');
            }}
          />
        );
      }

      if (preRegistration?.accessReport) {
        return (
          <BaSeButton
            buttonType="button"
            color={BaSeTheme.colors.institucionais.cinzaSebrae45}
            type={'secondary'}
            loadingText="Carregando..."
            isLoading={candidateLoading}
            value={'Acessar Relatório'}
            onClick={() => {
              getResultReport(preRegistration?.interview?.id).then((pdf) => {
                pdf &&
                  downloadPdf(
                    pdf,
                    `Relatório de entrevista - ${preRegistration?.interview?.candidate?.name}.pdf`,
                  );
              });
            }}
            width="150"
          />
        );
      }

      return okButtom();
    },
    [
      okButtom,
      optionsButtons,
      validPreRegistration,
      Nao,
      AceptedImportUser,
      candidateLoading,
      props,
      getResultReport,
      downloadPdf,
    ],
  );

  React.useEffect(() => {
    setCpf(props.interview?.candidate?.cpf);
    props.interview && setStatus(getStatusInterview(props.interview));
    props.preRegistration && setPreRegistration(props.preRegistration);
  }, [getStatusInterview, props]);

  const statusInputCpf = React.useCallback(() => {
    if (!cpf) {
      return 'normal'
    }
    if (cpfValidator(cpf)) {
      return 'valid'
    }
    return 'invalid'
  }, [cpf]);

  return (
    <ContentModal>
      <BaSeInput
        label="CPF:"
        inputMode='text'

        placeholder="Informe o CPF"
        onSpecificKeyDown={[
          {
            listenerKey: 'Enter',
            callback: () => find(),
          },
        ]}
        value={cpf}
        mask="cpf"
        isDisabled={props.showMessage || props.showEditMessage || disableForms}
        complement={cpfComplement}
        inputStatus={statusInputCpf()}
        onFocus={() => {
          navigator.clipboard.readText().then(async function (data) {
            const cleanedData = data.trim();

            navigator.clipboard.writeText(cpfMask(cleanedData));
          });
        }}
        onChange={(value) => {
          setCpf(value);
          setCpfComplement();
        }}
      />
      <BaSeSelect
        label="Selecione o Empretec"
        color={BaSeTheme.colors.institucionais.cinzaSebrae10}
        removeEmptyValue={true}
        selectedValueId={moduleSelect || moduleSelected?.id}
        values={modules.map((module) => ({
          ...module,
          id: module.id,
          label: module.name,
        }))}
        onChange={(value) => {
          setModuleSelect(value.id);
        }}
        isDisabled={props.showMessage || props.showEditMessage || disableForms}
      />

      {certificateInterview && (
        <AlertWrapper>
          <BaSeHeading6
            color={BaSeTheme.colors.feedbackInterface.atencao55}
            isBold={true}
          >
            Atenção!
          </BaSeHeading6>
          <BaSeSmall1>
            O(a) candidato(a) {interviewExpiredData?.candidate?.name} já teve
            participação no seminário do Empretec na data do dia{' '}
            {String(certificateInterviewData?.beginDate)
              .split('-')
              .reverse()
              .join('/')}{' '}
            ao dia{' '}
            {String(certificateInterviewData?.endDate)
              .split('-')
              .reverse()
              .join('/')}
            , portanto sua entrevista é “Certificada” e o candidato não poderá
            realizar novo cadastro e nova entrevista no sistema.
          </BaSeSmall1>
        </AlertWrapper>
      )}

      {interviewExpired && interviewExpiredData?.processingDate && (
        <AlertWrapper>
          <BaSeHeading6
            color={BaSeTheme.colors.feedbackInterface.atencao55}
            isBold={true}
          >
            Atenção!
          </BaSeHeading6>
          <BaSeSmall1>Entrevista expirada.</BaSeSmall1>
          <BaSeSmall1 color={BaSeTheme.colors.institucionais.cinzaSebrae45}>
            O(a) candidato(a) já realizou uma entrevista de seleção na data{' '}
            {format(
              new Date(interviewExpiredData?.processingDate),
              'dd/MM/yyyy',
            )}{' '}
            com o resultado da entrevista "
            {getStatusInterview(interviewExpiredData)}"
            {interviewExpiredData?.statusInterview === 'NAO_APTO'
              ? ' há mais de três meses'
              : ' há mais de doze meses'}
            .
          </BaSeSmall1>

          <BaSeSmall1 color={BaSeTheme.colors.institucionais.cinzaSebrae45}>
            {interviewExpiredData?.statusInterview === 'APTO'
              ? 'Tem certeza de que o candidato não participou do seminário Empretec e que deseja criar uma nova entrevista para esse(a) candidato(a)? '
              : 'Tem certeza que deseja criar uma nova entrevista para esse(a) candidato(a)?'}
          </BaSeSmall1>
        </AlertWrapper>
      )}
      {props.showMessage && (
        <AlertWrapper>
          <BaSeHeading6
            color={BaSeTheme.colors.feedbackInterface.atencao55}
            isBold={true}
          >
            Atenção!
          </BaSeHeading6>
          <BaSeSmall1>
            Existe entrevista válida (últimos{' '}
            {props.interview?.statusInterview === 'NAO_APTO' ? '3' : '12'} meses)
            para esse candidato na UF: {props.interview?.city?.uf?.initials} e
            ele está: {status}.
          </BaSeSmall1>
        </AlertWrapper>
      )}
      {props.showEditMessage && (
        <AlertWrapper>
          <BaSeHeading6
            color={BaSeTheme.colors.feedbackInterface.atencao55}
            isBold={true}
          >
            Atenção!
          </BaSeHeading6>
          <BaSeSmall1>
            Candidato já existe no sistema, para alterar as informações
            selecione a opção abaixo.
          </BaSeSmall1>
        </AlertWrapper>
      )}
      {preRegistration && (
        <AlertWrapper>
          <BaSeHeading6
            color={BaSeTheme.colors.feedbackInterface.atencao55}
            isBold={true}
          >
            Atenção!
          </BaSeHeading6>
          <BaSeSmall1 color={BaSeTheme.colors.institucionais.cinzaSebrae30}>
            <span
              dangerouslySetInnerHTML={{ __html: preRegistration.message }}
            ></span>
          </BaSeSmall1>
        </AlertWrapper>
      )}

      {preRegistration ? (
        resolverButtons(preRegistration)
      ) : interviewExpired ? (
        optionsButtons(Sim, Nao, false, false)
      ) : certificateInterview ? (
        okButtom()
      ) : (
        <ButtonWrapper>
          <BaSeButton
            buttonType="button"
            color={
              props.action === 'Continuar'
                ? BaSeTheme.colors.institucionais.azulSebrae10
                : BaSeTheme.colors.institucionais.cinzaSebrae45
            }
            type={props.action === 'Continuar' ? 'primary' : 'secondary'}
            loadingText="Carregando..."
            isLoading={candidateLoading}
            value={props.action}
            onClick={find}
          />
        </ButtonWrapper>
      )}
    </ContentModal>
  );
};
